import React, { useState, useEffect } from 'react';
import { Card, Badge, Button, List, Progress, Tag, Spin, message, Alert, Descriptions, Table } from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
  WarningOutlined,
  PlayCircleOutlined,
} from '@ant-design/icons';
import { CardSectionProps } from '../../../types/compositeCards';
import { ArtifactItemService } from '../../../services/api/artifactItemService';
import { StateMachineReportingService } from '../../../services/api/stateMachineReportingService';
import { ArtifactItem } from '../../../types/artifactItems';

/**
 * Artifact Item Detail Component
 * Displays artifact item details including name, description, and all properties
 */
export const ArtifactItemDetail: React.FC<CardSectionProps> = ({
  section,
  card,
  onAction
}) => {
  const [loading, setLoading] = useState(true);
  const [item, setItem] = useState<ArtifactItem | null>(null);
  const [properties, setProperties] = useState<Record<string, any>>({});

  const itemId = (card.data as any).item_id || (card.data as any).item?.id;
  const itemData = (card.data as any).item;

  useEffect(() => {
    if (itemId) {
      loadItemDetails();
    } else if (itemData) {
      // Use item data from card if available
      setItem(itemData);
      setProperties(itemData.item_data || itemData.properties || {});
      setLoading(false);
    }
  }, [itemId, itemData]);

  const loadItemDetails = async () => {
    try {
      setLoading(true);
      const [itemData, propertiesData] = await Promise.all([
        ArtifactItemService.getItemById(itemId),
        ArtifactItemService.getItemProperties(itemId).catch(() => ({}))
      ]);
      setItem(itemData);
      setProperties(propertiesData || itemData?.item_data || {});
    } catch (error) {
      console.error('Error loading item details:', error);
      message.error('Failed to load item details');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card size="small">
        <div style={{ textAlign: 'center', padding: 16 }}>
          <Spin />
        </div>
      </Card>
    );
  }

  const displayItem = item || itemData;
  if (!displayItem) {
    return (
      <Card size="small">
        <Alert message="Item not found" type="warning" />
      </Card>
    );
  }

  return (
    <Card
      size="small"
      title={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>Item Details: {displayItem.name || itemId?.substring(0, 8) || 'Unknown'}</span>
          <Button
            size="small"
            icon={<ReloadOutlined />}
            onClick={loadItemDetails}
          />
        </div>
      }
    >
      <Descriptions size="small" column={1} bordered>
        <Descriptions.Item label="Name">
          {displayItem.name || 'N/A'}
        </Descriptions.Item>
        <Descriptions.Item label="Description">
          {displayItem.description || 'N/A'}
        </Descriptions.Item>
        <Descriptions.Item label="State">
          <Tag color="blue">{displayItem.state || displayItem.current_state_id || 'Unknown'}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Artifact">
          {displayItem.artifact?.name || displayItem.artifact_id || 'N/A'}
        </Descriptions.Item>
        {Object.keys(properties).length > 0 && (
          <Descriptions.Item label="Properties">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
              {Object.entries(properties).map(([key, value]) => (
                <Tag key={key} color="cyan">
                  {key}: {String(value)}
                </Tag>
              ))}
            </div>
          </Descriptions.Item>
        )}
      </Descriptions>
    </Card>
  );
};

// TODO: Implement ArtifactItemParameters component
export const ArtifactItemParameters: React.FC<CardSectionProps> = ({
  section,
  card,
  onAction
}) => {
  return (
    <div style={{
      padding: '16px',
      backgroundColor: '#fef3c7',
      border: '1px solid #f59e0b',
      borderRadius: '8px',
      margin: '8px 0',
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '8px',
      }}>
        <div style={{
          width: '20px',
          height: '20px',
          backgroundColor: '#f59e0b',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontSize: '12px',
          fontWeight: 'bold',
        }}>
          TODO
        </div>
        <h4 style={{
          margin: 0,
          color: '#92400e',
          fontSize: '14px',
          fontWeight: '500',
        }}>
          Artifact Item Parameters
        </h4>
      </div>
      <p style={{
        margin: 0,
        color: '#92400e',
        fontSize: '12px',
        lineHeight: '1.4',
      }}>
        TODO: Implement artifact type specific parameters for the artifact item.
      </p>
    </div>
  );
};

/**
 * Artifact Item Action Component
 * Provides action buttons for artifact item operations (execute transition, update property, etc.)
 */
export const ArtifactItemAction: React.FC<CardSectionProps> = ({
  section,
  card,
  onAction
}) => {
  const [loading, setLoading] = useState(false);
  const [availableTransitions, setAvailableTransitions] = useState<any[]>([]);
  const [loadingTransitions, setLoadingTransitions] = useState(false);

  const itemId = (card.data as any).item_id || (card.data as any).item?.id;
  const artifactId = (card.data as any).artifact_id || (card.data as any).item?.artifact_id;
  const item = (card.data as any).item;

  useEffect(() => {
    if (itemId && artifactId) {
      loadAvailableTransitions();
    }
  }, [itemId, artifactId]);

  const loadAvailableTransitions = async () => {
    if (!itemId || !artifactId) return;
    
    try {
      setLoadingTransitions(true);
      const { StateMachineService } = await import('../../../services/api/stateMachineService');
      const transitions = await StateMachineService.getAvailableTransitions(artifactId, itemId);
      setAvailableTransitions(transitions || []);
    } catch (error) {
      console.error('Error loading available transitions:', error);
      // Don't show error message - just log it
    } finally {
      setLoadingTransitions(false);
    }
  };

  const handleExecuteTransition = (transitionId: string) => {
    if (onAction) {
      onAction('execute-transition', {
        itemId,
        artifactId,
        transitionId,
        item
      });
    }
  };

  const handleUpdateProperty = () => {
    if (onAction) {
      onAction('update-property', {
        itemId,
        artifactId,
        item
      });
    }
  };

  const handleViewHistory = () => {
    if (onAction) {
      onAction('view-history', {
        itemId,
        artifactId,
        item
      });
    }
  };

  return (
    <Card
      size="small"
      title="Item Actions"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {loadingTransitions ? (
          <div style={{ textAlign: 'center', padding: 8 }}>
            <Spin size="small" /> Loading transitions...
          </div>
        ) : availableTransitions.length > 0 ? (
          <div>
            <div style={{ marginBottom: 8, fontSize: '12px', color: '#666' }}>
              Available Transitions:
            </div>
            {availableTransitions.map((transition) => (
              <Button
                key={transition.id}
                type="primary"
                size="small"
                icon={<PlayCircleOutlined />}
                onClick={() => handleExecuteTransition(transition.id)}
                disabled={!transition.can_execute}
                style={{ marginBottom: 4, width: '100%' }}
              >
                {transition.name} → {transition.to_state_name || transition.to_state_id}
                {!transition.can_execute && ' (Cannot execute)'}
              </Button>
            ))}
          </div>
        ) : (
          <div style={{ fontSize: '12px', color: '#999', marginBottom: 8 }}>
            No available transitions
          </div>
        )}
        
        <Button
          size="small"
          onClick={handleUpdateProperty}
          style={{ width: '100%' }}
        >
          Update Properties
        </Button>
        
        <Button
          size="small"
          onClick={handleViewHistory}
          style={{ width: '100%' }}
        >
          View State History
        </Button>
      </div>
    </Card>
  );
};

/**
 * Artifact Item State Component
 * Displays artifact item state information and current state details
 */
export const ArtifactItemState: React.FC<CardSectionProps> = ({ card, onAction }) => {
  const [loading, setLoading] = useState(true);
  const [item, setItem] = useState<ArtifactItem | null>(null);
  const [currentState, setCurrentState] = useState<any>(null);

  const itemId = (card.data as any).item_id || (card.data as any).item?.id;

  useEffect(() => {
    if (itemId) {
      loadItemData();
    }
  }, [itemId]);

  const loadItemData = async () => {
    try {
      setLoading(true);
      const [itemData, stateData] = await Promise.all([
        ArtifactItemService.getItemById(itemId),
        ArtifactItemService.getItemCurrentState(itemId),
      ]);
      setItem(itemData);
      setCurrentState(stateData);
    } catch (error) {
      console.error('Error loading item data:', error);
      message.error('Failed to load item state');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card size="small">
        <div style={{ textAlign: 'center', padding: 16 }}>
          <Spin />
        </div>
      </Card>
    );
  }

  if (!item) {
    return (
      <Card size="small">
        <Alert message="Item not found" type="warning" />
      </Card>
    );
  }

  return (
    <Card
      size="small"
      title={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>Item State: {item.name || itemId.substring(0, 8)}</span>
          <Button
            size="small"
            icon={<ReloadOutlined />}
            onClick={loadItemData}
          />
        </div>
      }
    >
      <Descriptions size="small" column={1}>
        <Descriptions.Item label="Current State">
          <Tag color="blue">{currentState?.current_state_name || item.state || 'Unknown'}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="State Type">
          {currentState?.state_type || 'N/A'}
        </Descriptions.Item>
        <Descriptions.Item label="Last Transition">
          {currentState?.transitioned_at
            ? new Date(currentState.transitioned_at).toLocaleString()
            : 'N/A'}
        </Descriptions.Item>
      </Descriptions>
    </Card>
  );
};

/**
 * Artifact Workflow Progress Component
 * Displays workflow progress for an artifact
 */
export const ArtifactWorkflowProgress: React.FC<CardSectionProps> = ({ card, onAction }) => {
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState<any>(null);

  const artifactId = (card.data as any).artifact?.id || (card.data as any).artifactId;

  useEffect(() => {
    if (artifactId) {
      loadProgress();
    }
  }, [artifactId]);

  const loadProgress = async () => {
    try {
      setLoading(true);
      const progressData = await StateMachineReportingService.getWorkflowProgress(artifactId);
      setProgress(progressData);
    } catch (error) {
      console.error('Error loading workflow progress:', error);
      message.error('Failed to load workflow progress');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card size="small">
        <div style={{ textAlign: 'center', padding: 16 }}>
          <Spin />
        </div>
      </Card>
    );
  }

  if (!progress) {
    return (
      <Card size="small">
        <Alert message="Workflow progress unavailable" type="warning" />
      </Card>
    );
  }

  return (
    <Card
      size="small"
      title={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>Workflow Progress</span>
          <Button
            size="small"
            icon={<ReloadOutlined />}
            onClick={loadProgress}
          />
        </div>
      }
    >
      <Progress
        percent={progress.progress_percentage || 0}
        status="active"
        format={(percent) => `${percent}% Complete`}
      />
      <div style={{ marginTop: 16, display: 'flex', gap: 16 }}>
        <div>
          <div style={{ fontSize: '12px', color: '#666' }}>Total Items</div>
          <div style={{ fontSize: '18px', fontWeight: 'bold' }}>{progress.total_items || 0}</div>
        </div>
        <div>
          <div style={{ fontSize: '12px', color: '#666' }}>In Progress</div>
          <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1890ff' }}>
            {progress.in_progress_items || 0}
          </div>
        </div>
        <div>
          <div style={{ fontSize: '12px', color: '#666' }}>Completed</div>
          <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#52c41a' }}>
            {progress.completed_items || 0}
          </div>
        </div>
        <div>
          <div style={{ fontSize: '12px', color: '#666' }}>Blocked</div>
          <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#ff4d4f' }}>
            {progress.blocked_items || 0}
          </div>
        </div>
      </div>
    </Card>
  );
};

/**
 * Items Requiring Attention Component
 * Displays list of items that require user input
 */
export const ItemsRequiringAttention: React.FC<CardSectionProps> = ({ card, onAction }) => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<ArtifactItem[]>([]);

  const userId = (card.context as any)?.userId;
  const companyId = (card.data as any)?.company_id;

  useEffect(() => {
    loadItems();
  }, [userId, companyId]);

  const loadItems = async () => {
    try {
      setLoading(true);
      const itemsData = await ArtifactItemService.getItemsRequiringUserInput({
        user_id: userId,
        company_id: companyId,
        limit: 10,
      });
      setItems(itemsData);
    } catch (error) {
      console.error('Error loading items requiring attention:', error);
      message.error('Failed to load items requiring attention');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card size="small">
        <div style={{ textAlign: 'center', padding: 16 }}>
          <Spin />
        </div>
      </Card>
    );
  }

  const columns = [
    {
      title: 'Item',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: ArtifactItem) => text || `Item ${record.id.substring(0, 8)}`,
    },
    {
      title: 'State',
      dataIndex: 'state',
      key: 'state',
      render: (state: string) => <Tag color="orange">{state || 'Unknown'}</Tag>,
    },
    {
      title: 'Artifact',
      dataIndex: ['artifact', 'name'],
      key: 'artifact',
      render: (text: string) => text || 'N/A',
    },
  ];

  return (
    <Card
      size="small"
      title={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>
            <WarningOutlined style={{ color: '#faad14', marginRight: 8 }} />
            Items Requiring Attention ({items.length})
          </span>
          <Button
            size="small"
            icon={<ReloadOutlined />}
            onClick={loadItems}
          />
        </div>
      }
    >
      {items.length === 0 ? (
        <Alert message="No items requiring attention" type="success" />
      ) : (
        <Table
          dataSource={items}
          columns={columns}
          rowKey="id"
          pagination={false}
          size="small"
          onRow={(record) => ({
            onClick: () => {
              if (onAction) {
                onAction('view-item', { itemId: record.id });
              }
            },
            style: { cursor: 'pointer' },
          })}
        />
      )}
    </Card>
  );
};
